/**
 * Detect which content cluster a pathname belongs to. Used by
 * ClusterNav (secondary navbar) and ClusterFooter to render
 * cluster-specific internal links — see PRD "Dedicated Navbar &
 * Footer per Cluster".
 */
export type Cluster =
  | "calendar"
  | "hijri"
  | "prayer"
  | "tools"
  | "important"
  | null;

const RULES: Array<[Cluster, RegExp]> = [
  ["hijri", /^\/(en\/)?(kalender-hijriah|hijri-calendar|jadwal-imsak|jadwal-buka-puasa)(\/|$)/],
  ["prayer", /^\/(en\/)?jadwal-sholat(\/|$)/],
  ["calendar", /^\/(en\/)?(kalender|calendar|tanggal-merah|red-dates|libur-nasional|public-holidays|cuti-bersama|joint-leave|hari-ini|today)(\/|$)/],
  ["tools", /^\/(en\/)?(tools|kalkulator)(\/|$)/],
  ["important", /^\/(en\/)?(hari|harpitnas|zodiak)(\/|$)/],
];

export function detectCluster(pathname: string): Cluster {
  for (const [c, re] of RULES) if (re.test(pathname)) return c;
  return null;
}

/**
 * Per-cluster theme tokens. Maps each cluster to its accent colour
 * (foreground text, background tint, border). Used by Header / MegaMenu /
 * ClusterNav / ClusterFooter / Footer / Breadcrumb to keep the visual
 * identity consistent within a topical cluster.
 */
export type ClusterTheme = {
  accentText: string;     // text-* utility for accent colour
  accentBg: string;       // bg-*/10 utility for subtle accent fill
  accentBgSolid: string;  // bg-* utility for solid pill (use with accentFg)
  accentFg: string;       // foreground that pairs with accentBgSolid
  accentBorder: string;   // border-*/30
  accentRing: string;     // ring-*/30
  hover: string;          // hover:bg-*/10 hover:text-*
  label: { id: string; en: string };
};

const THEMES: Record<Exclude<Cluster, null>, ClusterTheme> = {
  calendar: {
    accentText: "text-primary",
    accentBg: "bg-primary/10",
    accentBgSolid: "bg-primary",
    accentFg: "text-primary-foreground",
    accentBorder: "border-primary/30",
    accentRing: "ring-primary/30",
    hover: "hover:bg-primary/10 hover:text-primary",
    label: { id: "Kalender", en: "Calendar" },
  },
  hijri: {
    accentText: "text-hijri",
    accentBg: "bg-hijri/10",
    accentBgSolid: "bg-hijri",
    accentFg: "text-white",
    accentBorder: "border-hijri/30",
    accentRing: "ring-hijri/30",
    hover: "hover:bg-hijri/10 hover:text-hijri",
    label: { id: "Hijriah & Puasa", en: "Hijri & Fasting" },
  },
  prayer: {
    accentText: "text-prayer",
    accentBg: "bg-prayer/10",
    accentBgSolid: "bg-prayer",
    accentFg: "text-prayer-foreground",
    accentBorder: "border-prayer/30",
    accentRing: "ring-prayer/30",
    hover: "hover:bg-prayer/10 hover:text-prayer",
    label: { id: "Jadwal Sholat", en: "Prayer Schedule" },
  },
  tools: {
    accentText: "text-tools",
    accentBg: "bg-tools/10",
    accentBgSolid: "bg-tools",
    accentFg: "text-white",
    accentBorder: "border-tools/30",
    accentRing: "ring-tools/30",
    hover: "hover:bg-tools/10 hover:text-tools",
    label: { id: "Tools", en: "Tools" },
  },
  important: {
    accentText: "text-zodiac",
    accentBg: "bg-zodiac/10",
    accentBgSolid: "bg-zodiac",
    accentFg: "text-white",
    accentBorder: "border-zodiac/30",
    accentRing: "ring-zodiac/30",
    hover: "hover:bg-zodiac/10 hover:text-zodiac",
    label: { id: "Hari Penting & Zodiak", en: "Notable Days & Zodiac" },
  },
};

export function getClusterTheme(cluster: Cluster): ClusterTheme {
  return THEMES[cluster ?? "calendar"];
}

export const CLUSTER_LANDING: Record<Exclude<Cluster, null>, string> = {
  calendar: "/kalender",
  hijri: "/kalender-hijriah",
  prayer: "/jadwal-sholat",
  tools: "/tools",
  important: "/hari",
};